import { CustomElement } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.types";
import { Event, EventHandler } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { Display, setDisplay } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { ALIGN_ITEMS, FLEX_CONTAINER_COLUMN, FLEX_ITEM, H_100, TEXT_ALIGN } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { createHtmlElement, empty } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { NoBubblesEventInit } from "@cervval.js/common/src/tools/event.tools";
import { TsDate, UnixTimestamp, UnixTimestampIndeterminateInterval } from "@cervval.js/common/src/tools/timestamps.tools.definitions";
import { NOW, TODAY } from "@cervval.js/common/src/tools/timestamps.tools.factory";
import { areSame, toDate } from "@cervval.js/common/src/tools/timestamps.tools.tsdate";
import { ObjectTools } from "@cervval.js/common/tools";
import { CjsButton } from "../../components/button/button";
import { CjsDateEditor } from "../../editors/editorsWithPicker/dateEditor/dateEditor";
import { CjsDateIntervalPicker } from "../dateIntervalPicker/dateIntervalPicker";
import { DatePickerOptions } from "../datePicker/datePicker";
import { Picker, PickerOptions } from "../picker";
import { I18nType, i18n } from "./i18n/i18n";

type IsUpdatingState = "startDate" | "endDate";

/** Drag selection interface */
interface DragSelection {

    /** A drag is processing */
    isOccurring: boolean;

    /** User is updating startDate or endDate during drag */
    isUpdating: IsUpdatingState;
}

export interface DateIntervalCalendarCalendarPickerSelection extends UnixTimestampIndeterminateInterval {
    /** Starting ts selected by user */
    start: UnixTimestamp | undefined;

    /**
     * Ending ts selected by user
     * ! value is beginOfNextDay than current day selected in component
     */
    finish: UnixTimestamp | undefined;
}

export interface DateIntervalCalendarPickerOptions extends PickerOptions {
    /** Options for all calendars */
    calendarsOptions?: Partial<DatePickerOptions>;

    /** Number of calendars displayed (prefer 2-6-12 calendars for better display) */
    calendarsCount: number;

    /** First calendar displayed will be at this date month (ex: 24/04/22 given => first month displayed: April) */
    firstCalendarDate: UnixTimestamp;

    /** 
     * if this option is true, when an interval is already selected, user can click on an edge and then click on another date to move the edge
     * clickToMoveEdges = true is not very intuitive, if user wants to pick an interval with the same start, he may not understand he needs to click on the end
     */
    clickToMoveEdges?: boolean;

    /** If true, click event will be dispatched even if value.finish is undefined */
    dispatchClickEventEvenIfFinishUndefined?: boolean;
}

export const defaultDateIntervalCalendarPickerOptions: DateIntervalCalendarPickerOptions = {
    /** Options for all calendars */
    calendarsOptions: {},
    /** Number of calendars displayed (prefer 2-6-12 calendars for better display) */
    calendarsCount: 2,
    /** First calendar displayed will be at this date month (ex: 24/04/22 given => first month displayed: April) */
    firstCalendarDate: NOW(),
    /** @inheritdoc */
    disabled: false
};

/** Store content of a Calendar of given month */
interface MonthCalendar {
    /** First month date of calendar */
    date: TsDate;
    /** Container of calendar */
    html: HTMLDivElement;
    /** Calendar component */
    cal: CjsDateIntervalPicker;
}

/**
 * @class CjsDateIntervalCalendarPicker @extends Picker
 * @classdesc Component for select range between two dates
 */
@CustomElement({
    name: "cjs-date-interval-calendar-picker",
    style: require("./dateIntervalCalendarPicker.scss"),
    template: require("./dateIntervalCalendarPicker.html"),
    i18n,
    elementCssClasses: [FLEX_CONTAINER_COLUMN, FLEX_ITEM, H_100]
})
export class CjsDateIntervalCalendarPicker extends Picker<DateIntervalCalendarCalendarPickerSelection, I18nType> {

    /** Dispatched when month changed */
    @Event(NoBubblesEventInit)
    public monthChangeEvent!: EventHandler;

    @TypedWebComponentOptions(defaultDateIntervalCalendarPickerOptions)
    public override readonly options!: WebComponentOptions<DateIntervalCalendarPickerOptions>;

    @TypedHtmlElements()
    public readonly components!: {
        startDateEditor: CjsDateEditor,
        startDateEditorLabel: HTMLDivElement,
        finishDateEditor: CjsDateEditor,
        finishDateEditorLabel: HTMLDivElement,
        calendarsContainer: HTMLDivElement,
        previousMonthBtn: CjsButton,
        nextMonthBtn: CjsButton
    };

    /** Shortcut for startDateEditor */
    public get startDateEditor(): CjsDateEditor { return this.components.startDateEditor; }

    /** Shortcut for finishDateEditor */
    public get finishDateEditor(): CjsDateEditor { return this.components.finishDateEditor; }

    /** Calendars displayed in components */
    public monthCalendars: MonthCalendar[] = [];

    /**
     * Store value of current interval selection
     */
    protected override _value: DateIntervalCalendarCalendarPickerSelection = {
        start: undefined,
        finish: undefined
    };

    /**
     * Store current drag selection
     * with which edge is manipulated
     */
    protected _dragSelection: DragSelection = {
        isOccurring: false,
        isUpdating: "endDate"
    };

    constructor(options?: Partial<DateIntervalCalendarPickerOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
    }

    /** When component is init */
    public override init(): void {

        // Hide buttons of date editors
        setDisplay(this.startDateEditor.getInput().button, Display.NONE);
        setDisplay(this.finishDateEditor.getInput().button, Display.NONE);
        this.finishDateEditor.options.setWithoutRefresh({ isFinishDate: true });

        // If firstCalendarDate isn"t given, set to TODAY
        if (!this.options.firstCalendarDate) {
            this.options.setWithoutRefresh({ firstCalendarDate: TODAY() });
        }

        // For smartPhone display, set calendars count to "1"
        if (document.body.clientWidth < 768) {
            this.options.setWithoutRefresh({ calendarsCount: 1 });
        }

        // Init component listeners
        this._initListeners();
    }

    /** Update component display */
    public override refresh(): void {
        super.refresh();
        this._refreshDateEditors();
        this._refreshCalendars();
    }

    /** Set drag selection from outside the component, handling specific behavior */
    public setDragSelection(dragSelection: DragSelection): void {
        this._dragSelection = dragSelection;
    }

    /** Picker value setter */
    public override setValue(value: DateIntervalCalendarCalendarPickerSelection): void {
        this._value = value;


        const startValue = this._value.start;
        // If startValue given, set first calendarDate to value start if new value isn't in range currently displayed
        if (startValue != null && !this.monthCalendars.some(monthCal => areSame(monthCal.date, startValue, "month"))) {
            this.options.setWithoutRefresh({ firstCalendarDate: this._value.start });
        }
    }

    /** Get selected interval values */
    public getValueInterval(): DateIntervalCalendarCalendarPickerSelection | undefined {

        // Return undefined if we don't have a correct interval
        if (!this._value.start) {
            return undefined;
        }
        return ObjectTools.clone(this._value);
    }

    /** Init component listeners */
    protected _initListeners(): void {
        // Listen to monthChange buttons
        this.components.previousMonthBtn.clickEvent.on(_ => this._navigateMonth("previous"));
        this.components.nextMonthBtn.clickEvent.on(_ => this._navigateMonth("next"));

        // On startDate editor value change
        this.components.startDateEditor.changeEvent.on(e => {
            if (this.disabled) { return; }// Don't allow changeEvent callback if component is disabled

            this._value.start = e.detail.value;
            // Refresh interval displayed in calendars
            this._renderCalendars();
            // Emit value change event
            this._dispatchOnPickerValueChange(this._value);
        });
        // On finishDate editor value change
        this.components.finishDateEditor.changeEvent.on(e => {
            if (this.disabled) { return; }// Don't allow changeEvent callback if component is disabled

            this._value.finish = e.detail.value;
            // Refresh interval displayed in calendars
            this._renderCalendars();
            // Emit value change event
            this._dispatchOnPickerValueChange(this._value);
        });

        // Refresh fillers when calendars container is resized
        new ResizeObserver(_ => this._refreshCalendarsLastRowItems()).observe(this.components.calendarsContainer);
    }

    /** Refresh dateEditors */
    protected _refreshDateEditors(): void {
        const dateFormat = this.i18n.labels.formatPlaceholder;
        // StartDate Editor
        this.startDateEditor.getInput().inputOptions.setAndRefreshIfChanged({
            enableClearButton: true,
            placeholderText: dateFormat
        });
        this.startDateEditor.pickerOptions.setWithoutRefresh({ ranges: this.options.calendarsOptions?.ranges });
        this.finishDateEditor.pickerOptions.setWithoutRefresh({ ranges: this.options.calendarsOptions?.ranges });

        this.components.startDateEditorLabel.innerHTML = this.i18n.labels.startDate;

        // FinishDate Editor
        this.finishDateEditor.getInput().inputOptions.setAndRefreshIfChanged({ enableClearButton: true, placeholderText: dateFormat });
        this.components.finishDateEditorLabel.innerHTML = this.i18n.labels.finishDate;

        /** Refresh editors values */
        this._refreshDateEditorsValueFromPickerValue({ dispatchEvents: true });
    }

    /** Refresh dateEditors value */
    protected _refreshDateEditorsValueFromPickerValue(p: { dispatchEvents: boolean }): void {
        this.startDateEditor.setValue(this._value.start, { ...p, refreshIfChanged: true });
        this.finishDateEditor.setValue(this._value.finish, { ...p, refreshIfChanged: true });
    }

    /** Refresh calendars */
    protected _refreshCalendars(): void {
        // Render calendars
        this._renderCalendars();
    }

    /** Renders calendars from options */
    protected _renderCalendars(): void {
        // Reset calendars
        this.monthCalendars = [];
        empty(this.components.calendarsContainer);

        // Create calendars
        let currentCalDate = toDate(this.options.firstCalendarDate);
        for (let calIndex = 0; calIndex < this.options.calendarsCount; calIndex++) {
            this.monthCalendars.push(this._renderCalendar(currentCalDate));
            // Increment month
            currentCalDate = currentCalDate.beginOfNext("month");
        }

        // Add calendars to their container
        this.components.calendarsContainer.append(...this.monthCalendars.map(cal => cal.html));
        // Add fill items
        this._refreshCalendarsLastRowItems();
    }

    /** Refresh last row calendars fillers count for correct display */
    protected _refreshCalendarsLastRowItems(): void {

        // Remove all fillers from calendarsContainer
        (Array.from(this.components.calendarsContainer.querySelectorAll(".cal-fill")) as HTMLElement[]).forEach(div => div.remove());

        // Count missing fillers, add-it to calendarsContainers
        const calsOnFirstRowCount = this.monthCalendars.map(cal => cal.cal.getBoundingClientRect().y).filter(y => this.monthCalendars[0].cal.getBoundingClientRect().y === y).length;
        const calsOnLastRowCount = this.monthCalendars.map(cal => cal.cal.getBoundingClientRect().y).filter(y => this.monthCalendars[this.monthCalendars.length - 1].cal.getBoundingClientRect().y === y).length;
        const diff = calsOnFirstRowCount - calsOnLastRowCount;
        if (diff !== 0) {
            for (let i = 0; i < diff; i++) {
                this.components.calendarsContainer.append(createHtmlElement("div", { cssClass: [FLEX_ITEM, "cal-fill"] }));
            }
        }
    }

    /** Render a calendar from a given date */
    protected _renderCalendar(date: TsDate): MonthCalendar {

        // Create new DateIntervalPicker
        const cal = new CjsDateIntervalPicker({
            displayOtherMonthsDays: false,
            displayTodayDate: true,
            enableHeightResize: false,
            calendarWeeks: false,
            monthYearTextAlign: TEXT_ALIGN.START,
            ...this.options.calendarsOptions ?? {},
            // Always fixed options, can't be overridden
            isFixedCalendar: true,
            enableKeyboardInput: false
        });

        // Set start day of calendar (never displayed)
        cal.setGridByTs(date.beginOf("month").uts);
        // Set interval of calendar
        cal.setValueInterval(this._value);

        // Create calendar container
        const html = createHtmlElement("div", {
            innerHTML: [cal],
            cssClass: [FLEX_CONTAINER_COLUMN, FLEX_ITEM, ALIGN_ITEMS.CENTER]
        });

        const monthCalendar: MonthCalendar = { cal, html, date };
        // Add listeners of calendar
        cal.dayCellOveredEvent.on(e => this._onCalendarDayCellOvered(monthCalendar, e.detail));
        cal.dateChangeEvent.on(e => this._onCalendarDayCellClicked(monthCalendar, e.detail));
        return monthCalendar;
    }

    /** Refresh display for all calendars when a cell is overed in a given calendar */
    protected _refreshOverDisplay(interval: UnixTimestampIndeterminateInterval, p?: { refreshCalendarInterval?: boolean }): void {
        if (!interval.finish) { return; }// Useless to render

        for (const monthCal of this.monthCalendars) {

            // If no drag currently or shouldn't refresh calendars interval
            if (!this._dragSelection.isOccurring && !p?.refreshCalendarInterval) {
                // Nothing todo
            } else {
                let start = interval.start;
                if (!start) {// If start is undefined, fill all days of calendars from beginning
                    start = monthCal.date.beginOfPrevious("month").uts;
                }

                // Set new interval of calendar
                monthCal.cal.setValueInterval({ start, finish: interval.finish }, { clearSelection: false });
            }

            // Always update cells of calendar from value interval
            monthCal.cal.updateCellsDisplay();
        }

    }

    /** When a calendar day cell is overed */
    protected _onCalendarDayCellOvered(cal: MonthCalendar, date: TsDate): void {
        const interval = this._getIntervalFromDate(date);
        this._refreshOverDisplay(interval);
    }

    /**
     * Get current valid interval from given date and current dragSelection
     * Will check if <startDate, finishDate> interval is valid
     */
    protected _getIntervalFromDate(date: TsDate): UnixTimestampIndeterminateInterval {
        const isUpdatingStartDate = this._dragSelection.isUpdating === "startDate";

        let start;
        if (isUpdatingStartDate) {
            start = date.uts;// Assign startDate if we are updating startDate
        } else {// Don't update startDate
            start = this._value.start;
        }

        let finish;
        if (isUpdatingStartDate) {// If we are updating startDate, don't update finishDate, if defined
            if (this._value.finish != null) {
                finish = this._value.finish;
            }
        } else {// Set finishDate
            finish = date.beginOfNext("day").uts;
        }

        // Check interval order
        if (start != null && finish != null && finish < start) {
            if (isUpdatingStartDate) {// Switch start<=>finish from date
                start = toDate(finish).beginOfPrevious("day").uts;
                finish = date.beginOfNext("day").uts;
            } else {// Switch finish<=>start from date
                finish = toDate(start).beginOfNext("day").uts;
                start = date.uts;
            }
        }
        return { start, finish };
    }

    /** When a calendar day cell is clicked */
    protected _onCalendarDayCellClicked(cal: MonthCalendar, date: TsDate): void {

        // On click start selection
        if (!this._dragSelection.isOccurring) {

            let isUpdating: IsUpdatingState;
            const asFinishDate = date.beginOfNext("day").uts;

            if (this.options.clickToMoveEdges && asFinishDate === this._value.finish) {// Click on finish date already selected
                isUpdating = "endDate";
            } else if (this.options.clickToMoveEdges && date.uts === this._value.start) {// Click on start date already selected
                isUpdating = "startDate";
            } else if (!this._value.start) {// First click on date, nothing already selected
                isUpdating = "endDate";
                this._value.start = date.uts;
                this._value.finish = undefined;
            } else {// First click on other date than start or finish date
                isUpdating = "endDate";
                this._value.start = date.uts;
                this._value.finish = undefined;
            }
            this._dragSelection = {
                isOccurring: true,
                isUpdating
            };
        } else {// Currently dragging interval, set value from clicked date
            if (this._dragSelection.isUpdating === "endDate") {
                this._value.finish = date.beginOfNext("day").uts;
            } else {
                this._value.start = date.uts;
            }

            // End drag
            this._dragSelection = {
                isOccurring: false,
                isUpdating: this._dragSelection.isUpdating
            };
        }

        // Check new values order, assign to current value
        const checkedInterval = this._getIntervalFromDate(date);
        // Update picker value
        this._value.start = checkedInterval.start;
        if (this._value.finish != null) {
            this._value.finish = checkedInterval.finish;
        }

        // Refresh intervalEditors
        this._refreshDateEditorsValueFromPickerValue({ dispatchEvents: false });

        // Update dragSelection state for all others calendars from new state
        this.monthCalendars.forEach(monthCal => {
            monthCal.cal.dragSelection.isOccurring = this._dragSelection.isOccurring;
            monthCal.cal.dragSelection.isUpdating = this._dragSelection.isUpdating;
        });

        // Refresh display of others calendars
        this._refreshOverDisplay(this._value);

        if (!this.options.dispatchClickEventEvenIfFinishUndefined && this._value.finish == null) {
            // we don't dispatch event on start click (this would close the picker in editor direct mode)
            return;
        }
        // Dispatch value change
        this._dispatchOnPickerValueChange(this._value);
    }

    /** Navigate to previous or next month */
    protected _navigateMonth(mode: "previous" | "next"): void {
        if (this.disabled) { return; }// Don't allow navigateMonth callback if component is disabled

        // Get month offset to apply
        const nextOrPreviousOffset = mode === "previous" ? -1 : 1;
        this.options.setWithoutRefresh({ firstCalendarDate: toDate(this.options.firstCalendarDate).add({ month: nextOrPreviousOffset }).uts });

        for (const monthCal of this.monthCalendars) {
            // Get calendar date from offset
            const newDate = monthCal.date.add({ month: nextOrPreviousOffset });
            // Refresh calendar state
            monthCal.cal.setGridByTs(newDate.uts);
            monthCal.date = newDate;
        }

        // Refresh display of cells in all calendars
        this._refreshOverDisplay(this._value, { refreshCalendarInterval: true });

        // Emit month has changed event
        this.monthChangeEvent.emit();
    }

}